// Iter 73 — Vendor Quotation Detail (Phase 2 step 1).
// Shows quote header + lines with catalogue drift + margin analytics +
// status transitions + documents.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, FileText, Check, X, Clock, FileArrowUp,
    DownloadSimple, Trash, Receipt, TrendUp, TrendDown,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    QUOTATION_STATUS_BADGE, QUOTATION_STATUS_LABEL,
    DOC_TYPE_LABEL, TDS_LABEL, humanBytes,
    formatAmount, formatMarginPct, isQuotationLocked,
} from "./vendor_constants";

export default function VendorQuotationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [quote, setQuote] = useState(null);
    const [allowedTransitions, setAllowedTransitions] = useState({});
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/vendor-quotations/${id}`),
            api.get(`/vendor-quotations/meta/enums`),
        ])
            .then(([qRes, mRes]) => {
                if (cancelled) return;
                setQuote(qRes.data);
                setAllowedTransitions(mRes.data.allowed_transitions || {});
            })
            .catch((err) => {
                if (!cancelled) {
                    toast.error(formatApiError(err, "Failed to load quotation"));
                    navigate("/vendor-quotations");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id, navigate, reloadKey]);

    const removeDoc = async (doc) => {
        if (!window.confirm(`Delete '${doc.file_name}'?`)) return;
        try {
            await api.delete(`/vendor-quotations/${id}/documents/${doc.id}`);
            toast.success("Document deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const openStatusDialog = (next) => { setPendingStatus(next); setStatusOpen(true); };

    if (loading) return <div className="px-8 py-12 text-slate-500" data-testid="vqt-detail-loading">Loading…</div>;
    if (!quote) return null;

    const badge = QUOTATION_STATUS_BADGE[quote.status] || QUOTATION_STATUS_BADGE.draft;
    const locked = isQuotationLocked(quote.status);
    const nextStates = allowedTransitions[quote.status] || [];

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="vqt-detail-page">
            <PageHeader
                eyebrow={`${quote.code} · ${quote.vendor_code} ${quote.vendor_name}`}
                title={quote.quotation_ref || `Quotation ${quote.code}`}
                description={
                    <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                        <Badge data-testid="vqt-status-badge" className={`${badge.className} hover:${badge.className}`}>
                            {badge.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                            {quote.currency} · Date {formatDate(quote.quotation_date)} · Valid until {formatDate(quote.valid_until)}
                        </span>
                        {locked && (
                            <Badge data-testid="vqt-locked-badge" variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                Financially locked
                            </Badge>
                        )}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button data-testid="vqt-back-btn" variant="outline" onClick={() => navigate(-1)} className="gap-1">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        <Button data-testid="vqt-open-vendor" variant="outline"
                            onClick={() => navigate(`/vendors/${quote.vendor_id}?tab=quotations`)} className="gap-1">
                            <ArrowLeft size={14} /> Vendor
                        </Button>
                        {can("vendor:write") && nextStates.length > 0 && (
                            <div className="flex items-center gap-1.5" data-testid="vqt-status-actions">
                                {nextStates.map((s) => (
                                    <Button
                                        key={s}
                                        data-testid={`vqt-status-to-${s}`}
                                        size="sm"
                                        variant={s === "accepted" ? "default" : "outline"}
                                        onClick={() => openStatusDialog(s)}
                                    >
                                        Mark {QUOTATION_STATUS_LABEL[s]}
                                    </Button>
                                ))}
                            </div>
                        )}
                        {can("vendor:write") && quote.status === "accepted" && (
                            <Button
                                data-testid="vqt-convert-to-po-btn"
                                size="sm"
                                onClick={async () => {
                                    if (!window.confirm("Convert this accepted quotation to a Purchase Order? The quote will become 'converted' and the new PO will be created in draft state.")) return;
                                    try {
                                        const r = await api.post(`/vendor-quotations/${quote.id}/convert-to-po`, {});
                                        toast.success(`PO ${r.data.code} created`);
                                        navigate(`/vendor-purchase-orders/${r.data.id}`);
                                    } catch (err) {
                                        toast.error(formatApiError(err, "Conversion failed"));
                                    }
                                }}
                                className="gap-1"
                            >
                                <FileText size={14} /> Convert to PO
                            </Button>
                        )}
                        {quote.purchase_order_id && (
                            <Button
                                data-testid="vqt-open-po-btn"
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/vendor-purchase-orders/${quote.purchase_order_id}`)}
                                className="gap-1"
                            >
                                <FileText size={14} /> Open PO ({quote.purchase_order_code})
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main column */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Margin analytics */}
                    <MarginPanel quote={quote} />

                    {/* Line items */}
                    <Section title="Line Items">
                        <div className="overflow-x-auto" data-testid="vqt-lines-table">
                            <table className="w-full text-sm">
                                <thead className="text-slate-600 text-xs">
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left px-3 py-2 font-medium">Service</th>
                                        <th className="text-left px-3 py-2 font-medium">Catalogue Map</th>
                                        <th className="text-right px-3 py-2 font-medium">Qty</th>
                                        <th className="text-right px-3 py-2 font-medium">Unit Price</th>
                                        <th className="text-right px-3 py-2 font-medium">GST %</th>
                                        <th className="text-right px-3 py-2 font-medium">Line Total</th>
                                        <th className="text-right px-3 py-2 font-medium">Catalogue Sell · Drift</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(quote.lines || []).map((ln) => (
                                        <tr key={ln.id} className="border-b border-slate-100" data-testid={`vqt-line-${ln.id}`}>
                                            <td className="px-3 py-3 align-top">
                                                <div className="font-medium text-slate-900 text-xs">{ln.vendor_service_code}</div>
                                                <div className="text-xs text-slate-600 mt-0.5">{ln.vendor_service_name}</div>
                                                {ln.description && <div className="text-[11px] text-slate-500 mt-0.5">{ln.description}</div>}
                                                {ln.hsn_sac_code && <div className="text-[10px] text-slate-400 mt-0.5 font-mono">HSN/SAC: {ln.hsn_sac_code}</div>}
                                            </td>
                                            <td className="px-3 py-3 align-top text-xs">
                                                {ln.catalogue_item_id ? (
                                                    <>
                                                        <div className="font-mono text-slate-700">{ln.catalogue_item_code}</div>
                                                        <div className="text-slate-500 text-[11px]">{ln.catalogue_item_name}</div>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-300">Unmapped</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-right font-mono text-xs">{ln.quantity}</td>
                                            <td className="px-3 py-3 text-right font-mono text-xs">{formatAmount(ln.unit_price, quote.currency)}</td>
                                            <td className="px-3 py-3 text-right font-mono text-xs text-slate-600">{Number(ln.gst_rate || 0).toFixed(2)}%</td>
                                            <td className="px-3 py-3 text-right font-mono text-slate-900">{formatAmount(ln.line_total, quote.currency)}</td>
                                            <td className="px-3 py-3 text-right text-xs">
                                                {ln.catalogue_item_id ? (
                                                    <div>
                                                        <div className="font-mono text-slate-700">{formatAmount(ln.catalogue_sell_price, quote.currency)}</div>
                                                        <CatalogueDrift drift={ln.catalogue_price_drift} current={ln.current_catalogue_sell_price} currency={quote.currency} />
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="text-xs text-slate-700">
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={5} className="text-right px-3 py-2 text-slate-500">Subtotal</td>
                                        <td className="text-right px-3 py-2 font-mono">{formatAmount(quote.subtotal, quote.currency)}</td>
                                        <td />
                                    </tr>
                                    <tr>
                                        <td colSpan={5} className="text-right px-3 py-2 text-slate-500">Per-line GST</td>
                                        <td className="text-right px-3 py-2 font-mono">{formatAmount(quote.line_gst_total, quote.currency)}</td>
                                        <td />
                                    </tr>
                                    {Number(quote.discount_amount) > 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-right px-3 py-2 text-rose-600">Less: Document discount</td>
                                            <td className="text-right px-3 py-2 font-mono text-rose-600">-{formatAmount(quote.discount_amount, quote.currency)}</td>
                                            <td />
                                        </tr>
                                    )}
                                    {Number(quote.tax_amount) > 0 && (
                                        <tr>
                                            <td colSpan={5} className="text-right px-3 py-2 text-slate-500">Document-level tax</td>
                                            <td className="text-right px-3 py-2 font-mono">{formatAmount(quote.tax_amount, quote.currency)}</td>
                                            <td />
                                        </tr>
                                    )}
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={5} className="text-right px-3 py-3 font-semibold text-slate-900">Grand Total</td>
                                        <td className="text-right px-3 py-3 font-mono font-semibold text-slate-900" data-testid="vqt-grand-total">
                                            {formatAmount(quote.grand_total, quote.currency)}
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </Section>

                    {/* Documents */}
                    <Section title="Documents">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-slate-500">
                                Attach vendor&apos;s PDF quote. PDF / DOC / DOCX / PNG / JPG up to 10 MB. Max 20 attachments.
                            </p>
                            {can("vendor:write") && (
                                <Button data-testid="vqt-upload-btn" size="sm" onClick={() => setUploadOpen(true)} className="gap-1">
                                    <FileArrowUp size={14} /> Upload
                                </Button>
                            )}
                        </div>
                        {(quote.documents || []).length === 0 ? (
                            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md">
                                No documents attached
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {quote.documents.map((d) => (
                                    <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`vqt-doc-${d.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-slate-900 truncate">{d.file_name}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 mr-1">
                                                    {DOC_TYPE_LABEL[d.document_type] || d.document_type}
                                                </Badge>
                                                {humanBytes(d.size_bytes)} · uploaded {formatDate(d.uploaded_at)}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <a data-testid={`vqt-doc-download-${d.id}`} href={d.file_url} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                                                <DownloadSimple size={12} /> Download
                                            </a>
                                            {can("vendor:write") && (
                                                <Button data-testid={`vqt-doc-delete-${d.id}`}
                                                    size="sm" variant="ghost" onClick={() => removeDoc(d)}
                                                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-7 w-7 p-0">
                                                    <Trash size={14} />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>

                    {/* Notes */}
                    {quote.notes && (
                        <Section title="Notes">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{quote.notes}</p>
                        </Section>
                    )}
                </div>

                {/* Sidebar rail */}
                <aside className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vqt-meta-card">
                        <h3 className="font-medium text-slate-900 mb-3">Record metadata</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Internal ID" value={quote.id} mono />
                            <MetaRow label="Display code" value={quote.code} mono />
                            <MetaRow label="Vendor" value={`${quote.vendor_code} · ${quote.vendor_name}`} />
                            <MetaRow label="Reference" value={quote.quotation_ref || "—"} />
                            <MetaRow label="Currency" value={quote.currency} />
                            <MetaRow label="Created" value={formatDate(quote.created_at)} />
                            <MetaRow label="Created by" value={quote.created_by_email} />
                            <MetaRow label="Updated" value={formatDate(quote.updated_at)} />
                            <MetaRow label="Updated by" value={quote.updated_by_email} />
                            {quote.accepted_at && <MetaRow label="Accepted" value={formatDate(quote.accepted_at)} />}
                            {quote.rejected_at && <MetaRow label="Rejected" value={formatDate(quote.rejected_at)} />}
                            {quote.expired_at && <MetaRow label="Expired" value={formatDate(quote.expired_at)} />}
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vqt-tds-card">
                        <h3 className="font-medium text-slate-900 mb-3">TDS Snapshot</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Applicable" value={quote.tds_applicable ? "Yes" : "No"} />
                            <MetaRow label="Section" value={TDS_LABEL[quote.tds_section] || quote.tds_section || "—"} />
                            <MetaRow label="Rate" value={`${Number(quote.tds_rate || 0).toFixed(2)}%`} />
                        </div>
                        <p className="text-[11px] text-slate-400 mt-3">
                            Captured from vendor profile at quote time. Used by future Vendor Bills / Payments to compute net payable.
                        </p>
                    </div>

                    {quote.rejection_reason && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-5" data-testid="vqt-rejection-card">
                            <h3 className="font-medium text-rose-900 mb-2 text-sm">Rejection Reason</h3>
                            <p className="text-xs text-rose-800">{quote.rejection_reason}</p>
                        </div>
                    )}
                </aside>
            </div>

            <DocumentUploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                quoteId={id}
                onUploaded={reload}
            />
            <StatusTransitionDialog
                open={statusOpen}
                onClose={() => setStatusOpen(false)}
                onSaved={reload}
                quote={quote}
                nextStatus={pendingStatus}
            />
        </div>
    );
}

function MarginPanel({ quote }) {
    const mapped = quote.mapped_line_count || 0;
    const unmapped = quote.unmapped_line_count || 0;
    const total = mapped + unmapped;
    const marginPositive = Number(quote.margin_amount || 0) >= 0;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vqt-margin-panel">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-slate-900">Margin Analytics</h3>
                <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">
                    {mapped}/{total} lines mapped to catalogue
                </Badge>
            </div>
            {mapped === 0 ? (
                <p className="text-xs text-slate-500 py-4 text-center" data-testid="vqt-margin-unmapped">
                    No catalogue-mapped line items — margin analysis is unavailable.
                    Map vendor services to catalogue products to compute cost-vs-sell margin.
                </p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <MarginStat label="Catalogue Value" value={formatAmount(quote.total_catalogue_value, quote.currency)}
                        hint="Sum of (qty × current catalogue sell price)" />
                    <MarginStat label="Mapped Cost" value={formatAmount(quote.mapped_cost_total, quote.currency)}
                        hint="Vendor quote total for mapped lines (excl. tax)" />
                    <MarginStat
                        label="Margin"
                        value={formatAmount(quote.margin_amount, quote.currency)}
                        tone={marginPositive ? "positive" : "negative"}
                    />
                    <MarginStat
                        label="Margin %"
                        value={formatMarginPct(quote.margin_pct)}
                        tone={marginPositive ? "positive" : "negative"}
                        icon={marginPositive ? <TrendUp size={12} /> : <TrendDown size={12} />}
                        testid="vqt-margin-pct"
                    />
                </div>
            )}
            {Number(quote.current_catalogue_value || 0) !== Number(quote.total_catalogue_value || 0) && mapped > 0 && (
                <div className="mt-4 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Note: Catalogue prices have drifted since this quote was created. Snapshot total: {formatAmount(quote.total_catalogue_value, quote.currency)} · current total: {formatAmount(quote.current_catalogue_value, quote.currency)}.
                </div>
            )}
        </div>
    );
}

function MarginStat({ label, value, hint, tone, icon, testid }) {
    const toneClass = tone === "positive"
        ? "text-emerald-700"
        : tone === "negative" ? "text-rose-700" : "text-slate-900";
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
            <div data-testid={testid} className={`mt-1 font-semibold font-mono inline-flex items-center gap-1 ${toneClass}`}>
                {icon}{value}
            </div>
            {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
        </div>
    );
}

function CatalogueDrift({ drift, current, currency }) {
    if (drift === undefined || drift === null) return null;
    const n = Number(drift);
    if (!Number.isFinite(n) || n === 0) return <div className="text-[10px] text-slate-400 mt-0.5">No drift</div>;
    const positive = n > 0;
    return (
        <div className={`text-[10px] mt-0.5 ${positive ? "text-emerald-700" : "text-rose-700"}`}>
            {positive ? "↑" : "↓"} now {formatAmount(current, currency)} ({positive ? "+" : ""}{formatAmount(n, currency)})
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-medium text-slate-900 mb-4">{title}</h3>
            {children}
        </div>
    );
}

function MetaRow({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-slate-500 shrink-0">{label}</div>
            <div className={`text-slate-900 text-right break-all ${mono ? "font-mono" : ""}`}>
                {value || "—"}
            </div>
        </div>
    );
}

function StatusTransitionDialog({ open, onClose, onSaved, quote, nextStatus }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    if (!quote || !nextStatus) return null;
    const needsReason = nextStatus === "rejected";

    const submit = async () => {
        if (needsReason && !reason.trim()) { toast.error("Reason is required when rejecting"); return; }
        setBusy(true);
        try {
            await api.patch(`/vendor-quotations/${quote.id}/status`, { status: nextStatus, reason });
            toast.success(`Status changed to ${QUOTATION_STATUS_LABEL[nextStatus]}`);
            onSaved();
            onClose();
            setReason("");
        } catch (err) {
            toast.error(formatApiError(err, "Status change failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vqt-status-dialog">
                <DialogHeader>
                    <DialogTitle>Change Status</DialogTitle>
                    <DialogDescription>
                        {quote.code} · {QUOTATION_STATUS_LABEL[quote.status]} → <strong>{QUOTATION_STATUS_LABEL[nextStatus]}</strong>
                        {nextStatus === "accepted" && " — financials will be locked after this."}
                    </DialogDescription>
                </DialogHeader>
                {needsReason && (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Rejection reason *</Label>
                        <textarea
                            data-testid="vqt-status-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            placeholder="Why is this quote being rejected?"
                        />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vqt-status-confirm" onClick={submit} disabled={busy}>
                        {busy ? "Saving…" : `Mark ${QUOTATION_STATUS_LABEL[nextStatus]}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DocumentUploadDialog({ open, onClose, quoteId, onUploaded }) {
    const fileRef = useRef(null);
    const [docType, setDocType] = useState("quotation");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Please choose a file"); return; }
        if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10 MB limit"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("document_type", docType);
            await api.post(`/vendor-quotations/${quoteId}/documents`, fd, {
                headers: { "Content-Type": undefined },
            });
            toast.success("Document uploaded");
            onUploaded();
            onClose();
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vqt-upload-dialog">
                <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                    <DialogDescription>
                        Vendor PDF or supporting doc (PDF / DOC / DOCX / PNG / JPG, max 10 MB).
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs text-slate-600 mb-1.5 inline-block">Document Type</Label>
                        <select
                            data-testid="vqt-upload-doctype"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
                        >
                            {Object.entries(DOC_TYPE_LABEL).map(([k, l]) => (
                                <option key={k} value={k}>{l}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs text-slate-600 mb-1.5 inline-block">File</Label>
                        <input
                            data-testid="vqt-upload-file"
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                            className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-slate-700 file:cursor-pointer"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vqt-upload-submit" onClick={submit} disabled={busy} className="gap-1">
                        <FileArrowUp size={14} /> {busy ? "Uploading…" : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
